import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';

const ASSURED_TIERS = [
  { sales: '₹10,000', rate: '65%' },
  { sales: '₹1,00,000', rate: '68%' },
  { sales: '₹2,50,000', rate: '70%' },
  { sales: '₹5,00,000', rate: '72.5%' },
  { sales: '₹10,00,000', rate: '75%' },
  { sales: '₹20,00,000', rate: '80%' },
];

const VARIABLE_TIERS = [
  { monthly: '₹1,000 – ₹4,999', rate: '65%' },
  { monthly: '₹5,000 – ₹14,999', rate: '68%' },
  { monthly: '₹15,000 – ₹29,999', rate: '70%' },
  { monthly: '₹30,000 – ₹59,999', rate: '73%' },
  { monthly: '₹60,000 – ₹99,999', rate: '77%' },
  { monthly: '₹1,00,000+', rate: '82%' },
];

function Tab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '10px 28px',
        fontWeight: 600,
        fontSize: 14,
        border: 'none',
        borderBottom: active ? '2px solid var(--saffron)' : '2px solid transparent',
        background: 'none',
        color: active ? 'var(--saffron)' : 'var(--ink3)',
        cursor: 'pointer',
        transition: 'var(--transition)',
      }}
    >
      {label}
    </button>
  );
}

export default function TutorPayment() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('how');

  return (
    <div style={{ background: 'var(--bg)', minHeight: 'calc(100vh - 60px)' }}>

      {/* ── Page Hero ── */}
      <div style={{ background: 'linear-gradient(135deg, var(--teal) 0%, #05514A 100%)', padding: '52px 0 40px', textAlign: 'center' }}>
        <div className="container">
          <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.15)', color: 'white', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '5px 16px', borderRadius: 999, marginBottom: 16 }}>
            Tutor Earnings
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 42, fontWeight: 700, color: 'white', marginBottom: 12 }}>How & When Do You Get Paid?</h1>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.8)', maxWidth: 520, margin: '0 auto' }}>
            Transparent earnings, timely payouts, and a rate that grows with you.
          </p>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border2)', position: 'sticky', top: 60, zIndex: 10 }}>
        <div className="container" style={{ display: 'flex', gap: 0 }}>
          <Tab label="How It Works" active={activeTab === 'how'} onClick={() => setActiveTab('how')} />
          <Tab label="Payout Schedule" active={activeTab === 'schedule'} onClick={() => setActiveTab('schedule')} />
          <Tab label="Earnings Rate" active={activeTab === 'rate'} onClick={() => setActiveTab('rate')} />
        </div>
      </div>

      <div className="container" style={{ padding: '48px 24px', maxWidth: 860 }}>

        {/* ── How It Works ── */}
        {activeTab === 'how' && (
          <div style={{ animation: 'fadeUp 0.25s ease' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, marginBottom: 8 }}>How Payouts Work</h2>
            <p style={{ color: 'var(--ink3)', fontSize: 15, marginBottom: 36 }}>Your earnings are credited to your MakeMyExpert wallet after each completed session. Here's how the full cycle flows.</p>

            {/* Steps */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 48 }}>
              {[
                { num: '01', title: 'Session Completed', desc: 'A learner completes a session you conducted. The booking status moves to "Completed".' },
                { num: '02', title: 'Earnings Credited to Wallet', desc: 'Your share of the session fee (after platform fee) is instantly credited to your MakeMyExpert wallet.' },
                { num: '03', title: 'Request a Payout', desc: 'Raise a payout request anytime after the 1st of the month. Minimum payout threshold is ₹500.' },
                { num: '04', title: 'Transfer by the 15th', desc: 'All payout requests received by the 3rd of the month are processed and transferred by the 15th.' },
              ].map((step, i, arr) => (
                <div key={step.num} style={{ display: 'flex', gap: 20, position: 'relative' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--saffron)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{step.num}</div>
                    {i < arr.length - 1 && <div style={{ width: 2, flex: 1, background: 'var(--border2)', margin: '4px 0' }} />}
                  </div>
                  <div style={{ paddingBottom: 28 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--ink)', marginBottom: 4 }}>{step.title}</div>
                    <div style={{ fontSize: 13, color: 'var(--ink3)', lineHeight: 1.7 }}>{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Payment Methods */}
            <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 16 }}>Payout Methods</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 40 }}>
              {[
                { icon: '🏦', label: 'Bank Transfer (NEFT/IMPS)', note: 'Any Indian bank account' },
                { icon: '📱', label: 'UPI', note: 'Instant transfers' },
                { icon: '💳', label: 'Razorpay Wallet', note: 'For quick access' },
              ].map(m => (
                <div key={m.label} className="card" style={{ padding: '20px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{m.icon}</div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 4 }}>{m.note}</div>
                </div>
              ))}
            </div>

            {/* What qualifies */}
            <div className="card" style={{ padding: '24px', background: 'var(--teal-lt)', border: '1.5px solid var(--teal)' }}>
              <h4 style={{ fontWeight: 700, color: 'var(--teal)', marginBottom: 12 }}>✅ What Sessions Qualify for Payout?</h4>
              <ul style={{ margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: 'var(--ink2)', lineHeight: 1.65 }}>
                <li>Sessions with status <strong>Completed</strong></li>
                <li>Sessions cancelled by the learner within the cancellation window (you still receive the cancellation fee)</li>
                <li>Group sessions where at least one attendee was present</li>
              </ul>
            </div>
          </div>
        )}

        {/* ── Payout Schedule ── */}
        {activeTab === 'schedule' && (
          <div style={{ animation: 'fadeUp 0.25s ease' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Payout Schedule</h2>
            <p style={{ color: 'var(--ink3)', fontSize: 15, marginBottom: 36 }}>We run a monthly payout cycle. Here's exactly when your money moves.</p>

            {/* Timeline */}
            <div className="card" style={{ padding: '32px', marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto' }}>
                {[
                  { date: '1st', label: 'Auto payout request generated', color: 'var(--teal)' },
                  { date: '3rd', label: 'Last day to submit payout request', color: 'var(--saffron)' },
                  { date: '15th', label: 'Transfer processed to your account', color: 'var(--teal)' },
                ].map((t, i, arr) => (
                  <React.Fragment key={t.date}>
                    <div style={{ textAlign: 'center', minWidth: 120 }}>
                      <div style={{ width: 48, height: 48, borderRadius: '50%', background: t.color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, margin: '0 auto 10px' }}>{t.date}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.5, maxWidth: 110, margin: '0 auto' }}>{t.label}</div>
                    </div>
                    {i < arr.length - 1 && (
                      <div style={{ flex: 1, height: 2, background: 'var(--border2)', minWidth: 40, marginBottom: 28 }} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Example */}
            <div className="card card-body" style={{ marginBottom: 32 }}>
              <h4 style={{ fontWeight: 700, marginBottom: 16, color: 'var(--ink)' }}>📅 Example</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, textAlign: 'center' }}>
                {[
                  { month: 'January', desc: 'You conduct sessions worth ₹8,000' },
                  { month: 'Feb 3rd', desc: 'Payout request submitted (auto or manual)' },
                  { month: 'Feb 15th', desc: '₹5,600 transferred to your bank (at 70% rate)' },
                ].map((ex, i) => (
                  <React.Fragment key={ex.month}>
                    <div style={{ padding: '16px', background: i === 2 ? 'var(--teal-lt)' : 'var(--bg2)', borderRadius: 10, border: `1.5px solid ${i === 2 ? 'var(--teal)' : 'var(--border2)'}` }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: i === 2 ? 'var(--teal)' : 'var(--ink)' }}>{ex.month}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 6, lineHeight: 1.5 }}>{ex.desc}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Rules */}
            <h3 style={{ fontWeight: 700, fontSize: 17, marginBottom: 14 }}>Key Rules</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                'Minimum payout threshold is ₹500. Balances below this carry over to the next cycle.',
                'Requests submitted after the 3rd are processed in the following month\'s cycle.',
                'Bank details must be verified before your first payout can be processed.',
                'Tax deducted at source (TDS) applies as per Indian income tax rules where applicable.',
              ].map(rule => (
                <div key={rule} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: 'var(--ink2)', lineHeight: 1.65 }}>
                  <CheckCircle size={15} style={{ color: 'var(--teal)', flexShrink: 0, marginTop: 2 }} />
                  {rule}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Earnings Rate ── */}
        {activeTab === 'rate' && (
          <div style={{ animation: 'fadeUp 0.25s ease' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Your Earnings Rate</h2>
            <p style={{ color: 'var(--ink3)', fontSize: 15, marginBottom: 12 }}>You earn a minimum of <strong>60%</strong> of every session fee. Your actual rate is determined monthly by whichever is higher — your <strong>Lifetime Assured Rate (A)</strong> or your <strong>Monthly Variable Rate (B)</strong>.</p>
            <div className="card" style={{ padding: '16px 20px', background: 'var(--saffron-lt)', border: '1.5px solid var(--saffron)', marginBottom: 36, fontSize: 13, color: 'var(--ink2)' }}>
              💡 <strong>Your rate = max(Rate A, Rate B)</strong> — evaluated fresh each month. The more you teach, the more you earn.
            </div>

            {/* Rate A */}
            <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>Rate A — Lifetime Assured Rate</h3>
            <p style={{ fontSize: 13, color: 'var(--ink3)', marginBottom: 16 }}>Based on your cumulative all-time earnings on the platform. Once unlocked, this rate never goes down.</p>
            <div className="card" style={{ overflow: 'hidden', marginBottom: 36 }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Cumulative Lifetime Earnings</th>
                    <th style={{ textAlign: 'center' }}>Assured Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {ASSURED_TIERS.map((t, i) => (
                    <tr key={t.sales} style={{ background: i % 2 === 0 ? 'var(--surface)' : 'var(--bg2)' }}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{t.sales}+</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ fontWeight: 700, color: 'var(--teal)', fontSize: 15 }}>{t.rate}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Rate B */}
            <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>Rate B — Monthly Variable Rate</h3>
            <p style={{ fontSize: 13, color: 'var(--ink3)', marginBottom: 16 }}>Based on your total earnings in the current calendar month. Resets each month.</p>
            <div className="card" style={{ overflow: 'hidden', marginBottom: 36 }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Monthly Earnings</th>
                    <th style={{ textAlign: 'center' }}>Variable Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {VARIABLE_TIERS.map((t, i) => (
                    <tr key={t.monthly} style={{ background: i % 2 === 0 ? 'var(--surface)' : 'var(--bg2)' }}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{t.monthly}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ fontWeight: 700, color: 'var(--saffron)', fontSize: 15 }}>{t.rate}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Reliability Bonus */}
            <div className="card card-body" style={{ marginBottom: 36, border: '1.5px solid var(--teal)', background: 'var(--teal-lt)' }}>
              <h4 style={{ fontWeight: 700, color: 'var(--teal)', marginBottom: 8 }}>🌟 Reliability Bonus</h4>
              <p style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.7, margin: 0 }}>
                New tutors who complete at least <strong>one session per month</strong> with <strong>zero no-shows</strong> for two consecutive months automatically get a rate boost to <strong>70%</strong> — regardless of sales volume.
              </p>
            </div>

            {/* What platform fee covers */}
            <h3 style={{ fontWeight: 700, fontSize: 17, marginBottom: 12 }}>What Does the Platform Fee Cover?</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 12 }}>
              {[
                { icon: '🛡️', label: 'Secure payments & fraud protection' },
                { icon: '📣', label: 'Marketing & learner acquisition' },
                { icon: '⚙️', label: 'Platform development & hosting' },
                { icon: '🎧', label: 'Customer support for both sides' },
              ].map(f => (
                <div key={f.label} className="card" style={{ padding: '16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 20 }}>{f.icon}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.5 }}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* ── Bottom CTA ── */}
      <div style={{ background: 'linear-gradient(135deg, var(--teal) 0%, #05514A 100%)', padding: '48px 0', textAlign: 'center', marginTop: 16 }}>
        <div className="container">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'white', marginBottom: 10 }}>Ready to start earning?</h2>
          <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: 24, fontSize: 15 }}>Join our growing community of experts earning on MakeMyExpert.</p>
          <button onClick={() => navigate('/auth?mode=register&role=expert')} className="btn btn-lg" style={{ background: 'white', color: 'var(--teal)', fontWeight: 700, gap: 8 }}>
            Sign Up as Expert <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
